import { generatePath } from 'react-router';
import { createApi } from '@reduxjs/toolkit/query/react';

import { URL } from 'api/constants';
import { addParamsToUrl } from 'utils/query';
import { BaseResponse, PaginationResponse } from 'types';

import { apiQuery } from '../../query';

import {
  TypedReportTable,
  GameAnalyticsPlayer,
  GameAnalyticsSummary,
  GameAnalyticsRequest,
  GameAnalyticsDetailsRequest,
} from './types';

export const gameAnalyticsApi = createApi({
  baseQuery: apiQuery,
  reducerPath: 'gameAnalyticsApi',
  endpoints: (build) => ({
    summary: build.query<GameAnalyticsSummary, { tenantId: string }>({
      transformResponse: (response: BaseResponse<GameAnalyticsSummary>) =>
        response.data,
      query: ({ tenantId }) => ({
        method: 'get',
        url: generatePath(URL.TENANT_GAME_ANALYTICS_SUMMARY, {
          id: tenantId,
        }),
      }),
    }),
    sessionsReport: build.query<TypedReportTable, GameAnalyticsRequest>({
      transformResponse: (response: BaseResponse<TypedReportTable>) =>
        response.data,
      query: ({ tenantId, ...params }) => {
        const path = generatePath(URL.TENANT_GAME_ANALYTICS_SESSIONS_REPORT, {
          id: tenantId,
        });
        return { method: 'get', url: addParamsToUrl(path, params) };
      },
    }),
    detailsReport: build.query<TypedReportTable, GameAnalyticsDetailsRequest>({
      transformResponse: (response: BaseResponse<TypedReportTable>) =>
        response.data,
      query: ({ kind, tenantId, ...params }) => {
        const path = generatePath(URL.TENANT_GAME_ANALYTICS_DETAILS_REPORT, {
          kind,
          id: tenantId,
        });
        return { method: 'get', url: addParamsToUrl(path, params) };
      },
    }),
    players: build.query<
      PaginationResponse<GameAnalyticsPlayer>,
      GameAnalyticsRequest
    >({
      transformResponse: (
        response: BaseResponse<PaginationResponse<GameAnalyticsPlayer>>
      ) => response.data,
      query: ({ tenantId, ...params }) => {
        const path = generatePath(URL.TENANT_GAME_ANALYTICS_PLAYERS, {
          id: tenantId,
        });
        return { method: 'get', url: addParamsToUrl(path, params) };
      },
    }),
  }),
});

export const {
  useSummaryQuery,
  usePlayersQuery,
  useDetailsReportQuery,
  useSessionsReportQuery,
} = gameAnalyticsApi;

export {
  GameAnalyticsGame,
  GAME_ANALYTICS_GAMES,
  GameAnalyticsPlayerStatus,
} from './types';

export type {
  ProgramHealth,
  ReportColumnType,
  TypedReportTable,
  GameAnalyticsPlayer,
  GameAnalyticsSummary,
  GameAnalyticsRequest,
  GameAnalyticsGameScore,
  GameAnalyticsDetailKind,
  GameAnalyticsStatusCounts,
  GameAnalyticsDetailsRequest,
} from './types';
